import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  Info,
  Loader2,
  Search,
  X,
  type LucideIcon,
} from 'lucide-react';
import { initials } from '@forever/shared';
import { staggerDelay, useCountUp } from '@/lib/motion';
import './ui.css';

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');

/* ---------------------------------- düğme ---------------------------------- */

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  block?: boolean;
  icon?: LucideIcon;
};

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  block = false,
  icon: IconComp,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        'fd-btn',
        `fd-btn--${variant}`,
        `fd-btn--${size}`,
        block && 'fd-btn--block',
        className,
      )}
    >
      {loading ? (
        <Loader2 size={16} className="fd-spin" aria-hidden />
      ) : IconComp ? (
        <IconComp size={16} aria-hidden />
      ) : null}
      {children}
    </button>
  );
}

export function IconButton({
  icon: IconComp,
  label,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { icon: LucideIcon; label: string }) {
  return (
    <button {...rest} aria-label={label} title={label} className={cx('fd-icon-btn', className)}>
      <IconComp size={18} aria-hidden />
    </button>
  );
}

/* ----------------------------------- kart ---------------------------------- */

export function Card({
  tone = 'plain',
  flat = false,
  className,
  children,
}: {
  tone?: 'plain' | 'accent' | 'warm' | 'cool';
  flat?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section
      className={cx(
        'fd-card',
        tone !== 'plain' && `fd-card--${tone}`,
        flat && 'fd-card--flat',
        className,
      )}
    >
      {children}
    </section>
  );
}

export function CardHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <header className="fd-card__header">
      <div>
        <h2 style={{ fontSize: 'var(--fd-text-md)' }}>{title}</h2>
        {subtitle ? (
          <p style={{ fontSize: 'var(--fd-text-sm)', color: 'var(--fd-color-text-muted)' }}>
            {subtitle}
          </p>
        ) : null}
      </div>
      {action}
    </header>
  );
}

export function CardBody({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('fd-card__body', className)}>{children}</div>;
}

/* ---------------------------------- alanlar --------------------------------- */

type FieldShellProps = {
  label?: string;
  hint?: string;
  error?: string | null;
  required?: boolean;
  children: ReactNode;
  htmlFor?: string;
};

export function FieldShell({ label, hint, error, required, children, htmlFor }: FieldShellProps) {
  return (
    <div className={cx('fd-field', error && 'fd-field--error')}>
      {label ? (
        <label className="fd-field__label" htmlFor={htmlFor}>
          {label}
          {required ? <span className="fd-field__required"> *</span> : null}
        </label>
      ) : null}
      {children}
      {error ? (
        <span className="fd-field__error" role="alert">
          <AlertTriangle size={13} aria-hidden />
          {error}
        </span>
      ) : hint ? (
        <span className="fd-field__hint">{hint}</span>
      ) : null}
    </div>
  );
}

export function Input({
  label,
  hint,
  error,
  required,
  id,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  hint?: string;
  error?: string | null;
}) {
  const auto = useId(id);
  return (
    <FieldShell label={label} hint={hint} error={error} required={required} htmlFor={auto}>
      <input
        {...rest}
        id={auto}
        required={required}
        aria-invalid={error ? true : undefined}
        className="fd-input"
      />
    </FieldShell>
  );
}

export function SearchInput({
  value,
  onChange,
  placeholder = 'Ara…',
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  label: string;
}) {
  return (
    <div className="fd-input-group" style={{ minWidth: 220 }}>
      <Search size={16} aria-hidden />
      <input
        className="fd-input"
        type="search"
        value={value}
        placeholder={placeholder}
        aria-label={label}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export function Select({
  label,
  hint,
  error,
  required,
  id,
  children,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & {
  label?: string;
  hint?: string;
  error?: string | null;
}) {
  const auto = useId(id);
  return (
    <FieldShell label={label} hint={hint} error={error} required={required} htmlFor={auto}>
      <select {...rest} id={auto} className="fd-select" aria-invalid={error ? true : undefined}>
        {children}
      </select>
    </FieldShell>
  );
}

export function Textarea({
  label,
  hint,
  error,
  required,
  id,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label?: string;
  hint?: string;
  error?: string | null;
}) {
  const auto = useId(id);
  return (
    <FieldShell label={label} hint={hint} error={error} required={required} htmlFor={auto}>
      <textarea
        {...rest}
        id={auto}
        className="fd-textarea"
        aria-invalid={error ? true : undefined}
      />
    </FieldShell>
  );
}

let idCounter = 0;
function useId(provided?: string): string {
  const ref = useRef<string | undefined>(undefined);
  if (!ref.current) {
    idCounter += 1;
    ref.current = provided ?? `fd-${idCounter}`;
  }
  return provided ?? ref.current;
}

/* --------------------------------- rozetler -------------------------------- */

export type PillTone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'warm' | 'cool';

export function Pill({
  children,
  tone = 'neutral',
  icon: IconComp,
}: {
  children: ReactNode;
  tone?: PillTone;
  icon?: LucideIcon;
}) {
  return (
    <span className={cx('fd-pill', `fd-pill--${tone}`)}>
      {IconComp ? <IconComp size={12} aria-hidden /> : null}
      {children}
    </span>
  );
}

/* ---------------------------------- avatar --------------------------------- */

export function Avatar({
  name,
  src,
  size = 34,
}: {
  name: string;
  src?: string | null;
  size?: number;
}) {
  if (src) {
    return (
      <img
        className="fd-avatar"
        src={src}
        alt=""
        width={size}
        height={size}
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <span
      className="fd-avatar"
      aria-hidden
      style={{ width: size, height: size, fontSize: size * 0.38 }}
    >
      {initials(name)}
    </span>
  );
}

/* --------------------------------- gösterge -------------------------------- */

export function StatTile({
  label,
  value,
  delta,
  tone = 'accent',
  onClick,
}: {
  label: string;
  value: string | number;
  delta?: string | null;
  tone?: 'accent' | 'warm' | 'cool' | 'plain';
  onClick?: () => void;
}) {
  const background = {
    accent: 'var(--fd-color-surface-accent)',
    warm: 'var(--fd-color-surface-warm)',
    cool: 'var(--fd-color-surface-cool)',
    plain: 'var(--fd-color-surface)',
  }[tone];

  const Tag = onClick ? 'button' : 'div';

  /*
   * Sayısal gösterge hedefine doğru yumuşakça çıkar: panel yenilendiğinde
   * hangi değerin değiştiği rakamın hareketinden anlaşılır. Metin değerlerde
   * (tarih, para birimi) animasyon yok.
   */
  const numeric = typeof value === 'number';
  const animated = useCountUp(numeric ? value : 0);
  const shown = numeric ? animated : value;

  return (
    <Tag
      className="fd-stat fd-rise"
      data-clickable={onClick ? 'true' : undefined}
      onClick={onClick}
      style={{
        background,
        borderColor: tone === 'plain' ? 'var(--fd-color-border)' : 'transparent',
      }}
      {...(onClick ? { type: 'button' as const, 'aria-label': `${label}: ${value}` } : {})}
    >
      <span className="fd-stat__label">{label}</span>
      {/* Sayaç dönerken ekran okuyucu her ara değeri okumasın. */}
      <span className="fd-stat__value" aria-hidden={numeric || undefined}>
        {shown}
      </span>
      {numeric ? <span className="fd-visually-hidden">{value}</span> : null}
      {delta ? <span className="fd-stat__delta">{delta}</span> : null}
    </Tag>
  );
}

/* ---------------------------------- tablo ---------------------------------- */

export type Column<T> = {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  /** Sayısal sütunlar sağa yaslanır ve sabit genişlikli rakam kullanır. */
  numeric?: boolean;
  width?: string;
};

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  selectedKey,
  loading,
  empty,
}: {
  columns: Array<Column<T>>;
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  selectedKey?: string | null;
  loading?: boolean;
  empty?: ReactNode;
}) {
  if (loading) {
    return (
      <div className="fd-table-wrap" style={{ padding: 'var(--fd-space-4)' }}>
        <div className="fd-stack">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="fd-skeleton" style={{ height: 36 }} />
          ))}
        </div>
      </div>
    );
  }

  if (rows.length === 0 && empty) {
    return <div className="fd-table-wrap">{empty}</div>;
  }

  return (
    <div className="fd-table-wrap">
      <table className="fd-table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} style={c.width ? { width: c.width } : undefined}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const key = rowKey(row);
            return (
              <tr
                key={key}
                style={staggerDelay(index, 28)}
                data-clickable={onRowClick ? 'true' : undefined}
                data-selected={selectedKey === key ? 'true' : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onRowClick(row);
                        }
                      }
                    : undefined
                }
              >
                {columns.map((c) => (
                  <td key={c.key} className={c.numeric ? 'fd-table__num' : undefined}>
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------- durum ekranları ------------------------------ */

export function EmptyState({
  icon: IconComp = Info,
  title,
  description,
  action,
}: {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="fd-empty">
      <span className="fd-empty__icon">
        <IconComp size={24} aria-hidden />
      </span>
      <h3 style={{ fontSize: 'var(--fd-text-lg)' }}>{title}</h3>
      {description ? (
        <p style={{ color: 'var(--fd-color-text-muted)', maxWidth: 460 }}>{description}</p>
      ) : null}
      {action}
    </div>
  );
}

export function ErrorState({
  title = 'Bir şeyler ters gitti',
  description = 'Bağlantınızı kontrol edip tekrar deneyin.',
  onRetry,
}: {
  title?: string;
  description?: string;
  onRetry?: () => void;
}) {
  return (
    <div className="fd-empty">
      <span
        className="fd-empty__icon"
        style={{ background: 'var(--fd-color-danger-subtle)', color: 'var(--fd-color-danger)' }}
      >
        <AlertTriangle size={24} aria-hidden />
      </span>
      <h3 style={{ fontSize: 'var(--fd-text-lg)' }}>{title}</h3>
      <p style={{ color: 'var(--fd-color-text-muted)', maxWidth: 460 }}>{description}</p>
      {onRetry ? (
        <Button variant="secondary" onClick={onRetry}>
          Tekrar dene
        </Button>
      ) : null}
    </div>
  );
}

export function Skeleton({
  height = 16,
  width = '100%',
  radius,
}: {
  height?: number;
  width?: number | string;
  radius?: number;
}) {
  return (
    <div className="fd-skeleton" style={{ height, width, borderRadius: radius }} aria-hidden />
  );
}

export function Note({
  children,
  tone = 'cool',
  icon: IconComp = Info,
}: {
  children: ReactNode;
  tone?: 'cool' | 'warm' | 'accent' | 'warning' | 'danger';
  icon?: LucideIcon;
}) {
  return (
    <div className={cx('fd-note', `fd-note--${tone}`)}>
      <IconComp size={16} aria-hidden style={{ flexShrink: 0, marginTop: 2 }} />
      <div>{children}</div>
    </div>
  );
}

export function ProgressBar({ value, label }: { value: number; label?: string }) {
  const pct = Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0));
  return (
    <div
      className="fd-progress"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct * 100)}
      aria-label={label}
    >
      <div className="fd-progress__fill" style={{ width: `${pct * 100}%` }} />
    </div>
  );
}

/* ---------------------------------- modal ---------------------------------- */

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  wide = false,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fd-modal-overlay" onClick={onClose} role="presentation">
      <div
        className={cx('fd-modal', wide && 'fd-modal--wide')}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="fd-modal__header">
          <h2 style={{ fontSize: 'var(--fd-text-lg)' }}>{title}</h2>
          <IconButton icon={X} label="Kapat" onClick={onClose} />
        </div>
        <div className="fd-modal__body">{children}</div>
        {footer ? <div className="fd-modal__footer">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

/** Yıkıcı işlemler için onay penceresi. */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Onayla',
  cancelLabel = 'Vazgeç',
  danger = false,
  loading = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p style={{ color: 'var(--fd-color-text-muted)' }}>{description}</p>
    </Modal>
  );
}

/* --------------------------------- sekmeler -------------------------------- */

export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: Array<{ value: T; label: string; count?: number }>;
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="fd-tabs" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.value}
          role="tab"
          type="button"
          aria-selected={t.value === value}
          className="fd-tab"
          onClick={() => onChange(t.value)}
        >
          {t.label}
          {typeof t.count === 'number' && t.count > 0 ? (
            <span style={{ marginLeft: 6, color: 'var(--fd-color-text-subtle)' }}>{t.count}</span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: Array<{ value: T; label: string }>;
  value: T;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="fd-segmented" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className="fd-segmented__item"
          aria-pressed={o.value === value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ---------------------------------- toast ---------------------------------- */

type Toast = { id: number; message: string; tone: 'success' | 'error' | 'info' };

type ToastContextValue = {
  show: (message: string, tone?: Toast['tone']) => void;
  success: (message: string) => void;
  error: (message: string) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counter = useRef(0);

  const show = useCallback((message: string, tone: Toast['tone'] = 'info') => {
    counter.current += 1;
    const id = counter.current;
    setToasts((prev) => [...prev, { id, message, tone }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3600);
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({ show, success: (m) => show(m, 'success'), error: (m) => show(m, 'error') }),
    [show],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fd-toasts" aria-live="polite" aria-atomic="false">
        {toasts.map((t) => (
          <div key={t.id} className={cx('fd-toast', `fd-toast--${t.tone}`)} role="status">
            {t.tone === 'success' ? (
              <Check size={16} aria-hidden />
            ) : t.tone === 'error' ? (
              <AlertTriangle size={16} aria-hidden />
            ) : (
              <Info size={16} aria-hidden />
            )}
            <span>{t.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast, ToastProvider içinde kullanılmalı');
  return ctx;
}

/* ------------------------------- açılır kapanır ------------------------------- */

export function Collapsible({
  title,
  children,
  defaultOpen = false,
}: {
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        type="button"
        className="fd-row fd-row--between"
        style={{ width: '100%', padding: 'var(--fd-space-3) 0' }}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span style={{ fontWeight: 600 }}>{title}</span>
        <ChevronDown
          size={18}
          aria-hidden
          style={{
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform var(--fd-duration-fast) var(--fd-ease-standard)',
          }}
        />
      </button>
      {open ? <div className="fd-enter">{children}</div> : null}
    </div>
  );
}

export function Divider() {
  return <hr className="fd-divider" />;
}

export { cx };
